import { DialogComponent } from '@theme/dialog';
import { StandardEvents } from '@shopify/events';

/**
 * Keeps `data-show-form` (and the measurement fields' `disabled` state) in
 * sync with the selected variant, and keeps itself in sync with
 * variant-picker's section-morph updates (the same pattern product-price.js
 * and media-gallery.js use). The fields themselves live inside the
 * <custom-measurements-modal> dialog rendered as this element's only child
 * — see snippets/custom-measurements-modal.liquid.
 */
class CustomMeasurementsToggle extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section');
    if (!this.section) return;
    this.section.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    this.section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    if (!event.promise) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;
        const updated = detail.html.querySelector(`custom-measurements-toggle[data-block-id="${this.dataset.blockId}"]`);
        if (!updated) return;

        const shouldOpen = this.dataset.openAfterVariantChange === 'true' && updated.dataset.showForm === 'true';
        this.replaceWith(updated);
        if (shouldOpen) updated.querySelector('custom-measurements-modal')?.showDialog?.();
      })
      .catch(() => {});
  };
}

if (!customElements.get('custom-measurements-toggle')) {
  customElements.define('custom-measurements-toggle', CustomMeasurementsToggle);
}

// Treat the unselected Custom Measurements pill as an action button. Capture
// the click before the variant picker can select it, keeping the pill visually
// unselected while opening the questionnaire reliably.
document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const option = target.closest('label')?.querySelector('input[type="radio"]');
    if (!(option instanceof HTMLInputElement)) return;
    if (option.value.trim().toLowerCase() !== 'custom measurements') return;

    const section = option.closest('.shopify-section');
    const toggle = section?.querySelector('custom-measurements-toggle');
    if (!toggle) return;

    // When Traditional is not selected yet, let the variant picker handle this
    // first click, then open the modal as soon as the updated section arrives.
    if (toggle.dataset.showForm !== 'true') {
      toggle.dataset.openAfterVariantChange = 'true';
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    toggle.querySelector('custom-measurements-modal')?.showDialog?.();
  },
  true
);

/**
 * The Custom Measurements modal. assets/product-form.js opens this instead
 * of adding to cart when the selected variant is a Custom Measurements
 * variant (see ProductFormComponent#handleSubmit). Confirming re-submits
 * that same product form (flagged as buyer-confirmed) so the fields'
 * `form="..."` association carries their values through as cart line item
 * properties — the exact same add-to-cart path every other variant uses.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLButtonElement} confirmButton
 * @property {HTMLInputElement[]} field
 * @property {HTMLElement} errorMessage
 *
 * @extends DialogComponent<Refs>
 */
class CustomMeasurementsModal extends DialogComponent {
  requiredRefs = ['dialog', 'confirmButton', 'field', 'errorMessage'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Declarative on:click target for the confirm button.
   *
   * Every measurement field must be filled in before the item can be added
   * to cart — a garment can't be cut from a half-filled measurement set —
   * so this blocks the re-submit and points at the first empty field
   * instead of silently accepting an incomplete one. Once every field has a
   * value it flags the product form as buyer-confirmed and re-submits it;
   * ProductFormComponent#handleSubmit lets that second submission through
   * instead of reopening this modal.
   */
  confirmMeasurements() {
    const firstEmptyField = this.refs.field.find((field) => field.value.trim() === '');

    for (const field of this.refs.field) {
      if (field === firstEmptyField) {
        field.setAttribute('aria-invalid', 'true');
      } else {
        field.removeAttribute('aria-invalid');
      }
    }
    this.refs.errorMessage.hidden = !firstEmptyField;

    if (firstEmptyField) {
      firstEmptyField.focus();
      return;
    }

    const form = document.getElementById(this.dataset.productFormId ?? '');
    if (!(form instanceof HTMLFormElement)) return;

    form.dataset.measurementsConfirmed = 'true';
    form.requestSubmit();
  }

  /**
   * Closes the modal once the confirmed add-to-cart succeeds; stays open
   * (with the buyer's entries intact) if the request errors.
   *
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[custom-measurements] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('custom-measurements-modal')) {
  customElements.define('custom-measurements-modal', CustomMeasurementsModal);
}
